import { Box, Card, Divider, Grid, Loader, Stack, Text, Title } from "@mantine/core";
import { useMsal } from "@azure/msal-react";
import { getHeaders } from "../../api";
import { useLogin, getToken, isInternalEmployee } from "../../authConfig";
import { useEffect, useState } from "react";
import { getFileExtension } from "./getFileExtenstion";
import { PdfViewer } from "../pdf-viewer";
import { VideoViewer } from "../video-viewer";
import { CitationImage } from "../citation-image";
import { CitationMarkdownViewer } from "../citation-markdown-viewer";
import { Citation } from "../types";
import { MdOpenInNew } from "react-icons/md";
import { t } from "i18next";

export interface AssetViewerProps {
    citation?: Citation;
}

type Asset = AlgoliaAsset | PublicationInternalAsset | PublicationExternalAsset | IntellumAsset | VideoAudioAsset;

interface AlgoliaAsset {
    kind: "algolia";
    citation: Citation;
    path: string;
}

interface PublicationInternalAsset {
    kind: "publication-internal";
    citation: Citation;
    path: string;
}

interface PublicationExternalAsset {
    kind: "publication-external";
    citation: Citation;
    publicationUrl: string;
}

interface IntellumAsset {
    kind: "intellum";
    citation: Citation;
    intellumUrl: string;
}

interface VideoAudioAsset {
    kind: "video-audio";
    citation: Citation;
    mediaType: "video" | "audio";
    path: string;
}

export const AssetViewer = ({ citation }: AssetViewerProps) => {
    const client = useLogin ? useMsal().instance : undefined;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [citationSrc, setCitationSrc] = useState<string | null>(null);
    const [asset, setAsset] = useState<Asset | null>(null);

    const fetchCitation = async (path: string) => {
        try {
            setIsLoading(true);
            const token = client ? await getToken(client) : undefined;
            const response = await fetch(path, {
                method: "GET",
                headers: await getHeaders(token)
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch citation: ${response.statusText}`);
            }
            const citationContent = await response.blob();
            let citationObjectUrl = URL.createObjectURL(citationContent);

            // Get hash from the URL as it may contain #page=N
            // which helps browser PDF renderer jump to correct page N
            const pageNumber = path.indexOf("#") ? path.split("#")[1] : "";
            // Add hash back to the new blob URL
            if (pageNumber) {
                citationObjectUrl += "#" + pageNumber;
            }

            setCitationSrc(citationObjectUrl);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching citation:", error);
            setCitationSrc(null);
        } finally {
            setIsLoading(false); // Set loading state to false
        }
    };

    useEffect(() => {
        console.log("Asset changed:", asset);
        if (asset?.kind === "algolia" || asset?.kind === "publication-internal") {
            fetchCitation(asset.path);
        } else {
            setCitationSrc(null);
            setIsLoading(false);
        }
    }, [asset]);

    useEffect(() => {
        const determineHowToShow = async () => {
            if (citation?.tooltip.publicationId) {
                if (client && (await isInternalEmployee(client))) {
                    setAsset({ kind: "publication-internal", citation: citation, path: citation.path });
                } else {
                    setAsset({ kind: "publication-external", citation: citation, publicationUrl: citation.tooltip.publicationUrl! });
                }
            } else if (citation?.source.intellumUrl) {
                setAsset({ kind: "intellum", citation: citation, intellumUrl: citation.source.intellumUrl });
            } else if (citation?.tooltip.mediaType === "video" || citation?.tooltip.mediaType == "audio") {
                setAsset({ kind: "video-audio", citation: citation, mediaType: citation?.tooltip.mediaType, path: citation.path });
            } else if (citation?.path) {
                setAsset({ kind: "algolia", citation: citation, path: citation.path });
            }
        };

        determineHowToShow().catch(console.error);
    }, [citation]);

    const renderFileViewer = () => {
        if (isLoading) {
            return <Loader size={16} type="dots" />;
        }

        if (asset?.kind == "video-audio") {
            return <VideoViewer mediaType={asset.mediaType} url={asset.path} />;
        }

        if (asset?.kind == "publication-external") {
            return (
                <Box>
                    <a href={asset.publicationUrl} target="_blank" rel="noopener noreferrer">
                        <MdOpenInNew size={14} />
                        View Publication
                    </a>
                </Box>
            );
        }

        if (asset?.kind == "intellum") {
            return (
                <Box>
                    <a href={asset.intellumUrl} target="_blank" rel="noopener noreferrer">
                        <MdOpenInNew size={14} />
                        View external source
                    </a>
                </Box>
            );
        }

        const fileExtension = getFileExtension(citation?.path || "");

        switch (fileExtension) {
            case "png":
                return <CitationImage src={citationSrc} />;

            case "md":
                return <CitationMarkdownViewer src={citation!.path} />;

            case "trs": {
                return <VideoViewer mediaType={citation!.tooltip.mediaType} url={citation!.path || ""} />;
            }

            case "pb": {
                return <div>Playbook currently not supported</div>;
            }

            default:
                return <PdfViewer file={citationSrc} />;
        }
    };

    return (
        <Box style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ flexGrow: 1 }}>{renderFileViewer()}</div>
            <Grid mt="md">
                <Grid.Col>
                    <Stack gap="sm">
                        <Stack gap="xs">
                            <Text fw={500}>{t("Publication date")}</Text>
                            <Text size="sm">
                                {citation?.lastModifiedTimestamp != null ? new Date(Date.parse(citation.lastModifiedTimestamp)).toLocaleDateString() : "-"}
                            </Text>

                            {citation?.tooltip.category && citation?.tooltip.category != "-" && (
                                <>
                                    <Text fw={500}>{t("Resource type")}</Text>
                                    <Text size="sm">{citation?.tooltip.category}</Text>
                                </>
                            )}

                            {citation?.tooltip.referenceNumber && (
                                <>
                                    {" "}
                                    <Text fw={500}>{t("Reference Number")}</Text>
                                    <Text size="sm">{citation?.tooltip.referenceNumber}</Text>
                                </>
                            )}
                        </Stack>
                    </Stack>
                </Grid.Col>
            </Grid>
        </Box>
    );
};
